"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface StaffFormData {
  name: string;
  phone: string;
  email: string;
  password: string;
}

export interface StaffUpdateFormData {
  name: string;
  phone: string;
  email: string;
}

interface StaffFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formData: StaffFormData;
  onFormDataChange: (data: StaffFormData) => void;
  onSave: () => void;
  onCancel: () => void;
  mode: "add" | "edit";
  isSubmitting?: boolean;
}

export function StaffFormDialog({
  open,
  onOpenChange,
  formData,
  onFormDataChange,
  onSave,
  onCancel,
  mode,
  isSubmitting = false,
}: StaffFormDialogProps) {
  const isEdit = mode === "edit";
  const fieldPrefix = isEdit ? "edit-" : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Staff Member" : "Add New Staff Member"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update staff member details. Leave password blank to keep current password."
              : "Enter staff member details. Fields marked with * are required."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor={`${fieldPrefix}name`}>Full Name *</Label>
            <Input
              id={`${fieldPrefix}name`}
              value={formData.name}
              onChange={(e) =>
                onFormDataChange({ ...formData, name: e.target.value })
              }
              placeholder="Enter full name"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor={`${fieldPrefix}email`}>Email *</Label>
            <Input
              id={`${fieldPrefix}email`}
              type="email"
              value={formData.email}
              onChange={(e) =>
                onFormDataChange({ ...formData, email: e.target.value })
              }
              placeholder="staff@example.com"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor={`${fieldPrefix}password`}>
              Password {isEdit ? "" : "*"}
            </Label>
            <Input
              id={`${fieldPrefix}password`}
              type="password"
              value={formData.password}
              onChange={(e) =>
                onFormDataChange({ ...formData, password: e.target.value })
              }
              placeholder={isEdit ? "Leave blank to keep current password" : "Minimum 6 characters"}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor={`${fieldPrefix}phone`}>Phone *</Label>
            <Input
              id={`${fieldPrefix}phone`}
              placeholder="+974 XXXX XXXX"
              value={formData.phone}
              onChange={(e) =>
                onFormDataChange({ ...formData, phone: e.target.value })
              }
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={isSubmitting}>
            {isSubmitting
              ? "Saving..."
              : isEdit
                ? "Save Changes"
                : "Add Staff Member"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
